import React from 'react';
import {
  Button,
  Collapse,
  FormControl,
  FormLabel,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { FormProvider, UseFieldArrayMethods } from 'react-hook-form';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Form } from '../../../form';
import { FormSectionInfo } from '../../../form/components';
import { RadioLabelled } from '../../../../packages/radio-labelled';
import { RHFRadioGroup } from '../../../../packages/react-hook-form/radio-group/radio-group';
import { RadioConvertUtils } from '../../../../utils/radio/radio';
import { useScrollToTop } from '../../../../utils/hooks';
import { ErrorMessage } from '../../../error-message';
import { useSelectConfiguration } from '../../../app/use-app';
import { FormSectionContainer } from '../../../form/components/form-section-container/form-section-container';
import { PersonInputGroup } from '../person-input-group/person-input-group';
import { Stack } from '../../../stack/stack';
import { hasFieldsWithValues } from '../../../../utils';
import { PhysicalPersonFieldInput } from './components/physical-person-input';
import { LegalShareholderInput } from './components/legal-entity-input';
import { useOwnerShares, useResetLegalShareholders } from './use-owner-shares';
import { calculateShareCapital } from './owner-shares-utils';
import { OwnerSharesSubmitButton } from './components/submit-button';
import { RemainingSharesLabel } from './components/remaining-shares-label/remaining-shares-label';
import {
  OwnerSharesFieldLabels,
  OwnerSharesFieldNames,
} from './owner-shares-types';

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(5),
  },
  subSection: {
    marginBottom: theme.spacing(4),
  },
  addEntityButton: {
    marginTop: theme.spacing(2),
  },
  remainingSharesError: {
    marginTop: theme.spacing(2),
  },
  legalShareholders: {
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(4),
    },
  },
}));

// eslint-disable-next-line @typescript-eslint/ban-types
function containsNonEmptyObject(objects: object[]) {
  return objects.reduce(
    (hasNonEmptyObj, currentObj) =>
      hasNonEmptyObj || hasFieldsWithValues(currentObj),
    false,
  );
}

export const BuyProcessFormOwnerShares = () => {
  const {
    physicalPeopleFieldArrayName,
    legalShareholdersFieldArrayName,
    physicalPeopleFieldArray,
    legalShareholdersFieldArray,
    formMethods,
    onSubmit,
    id,
    onBack,
    onForward,
    isUpdating,
  } = useOwnerShares();

  const [showLegalShareholders, setShowLegalShareholders] = React.useState(
    legalShareholdersFieldArray.fields.length > 0,
  );

  const onInsertField = (fieldArray: UseFieldArrayMethods) => {
    fieldArray.insert(fieldArray.fields.length, {});
  };

  const classes = useStyles();

  useScrollToTop();

  useResetLegalShareholders(
    showLegalShareholders,
    formMethods.getValues().legalShareholders,
    legalShareholdersFieldArray.remove,
    id,
  );

  const config = useSelectConfiguration();

  return (
    <Form onSubmit={onSubmit}>
      <input ref={formMethods.register} name="remainingShares" hidden />

      <div>
        <FormSectionInfo.InfoBoxTitled title="Aktiekapital">
          <Typography>
            Du kan själv välja önskat aktiekapital nedan och kostnaden är
            densamma. Nya regler som gäller från den 1 januari 2020 möjliggör
            ett aktiekapital på lägst 25 000 kr.
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer className={classes.section}>
          <RHFRadioGroup
            onChange={(_, value) => {
              formMethods.setValue(
                'shareCapital',
                calculateShareCapital(Number(value)),
              );
            }}
            label={OwnerSharesFieldLabels.numberShares}
            control={formMethods.control}
            name={OwnerSharesFieldNames.numberShares}
            required
          >
            <RadioLabelled
              value={OwnerSharesFieldNames.shares250}
              label={OwnerSharesFieldLabels.shares250}
            />
            <RadioLabelled
              value={OwnerSharesFieldNames.shares500}
              label={OwnerSharesFieldLabels.shares500}
            />
          </RHFRadioGroup>

          <ErrorMessage
            className={classes.remainingSharesError}
            condition={!!formMethods.errors.remainingShares}
            onClose={() => formMethods.clearErrors('remainingShares')}
          >
            {formMethods.errors.remainingShares?.message}
          </ErrorMessage>
        </FormSectionContainer>
      </div>

      <div>
        <FormSectionInfo.InfoBoxTitled title="Om olika typer av aktieägare">
          <Typography>
            Nedan anges bolagets nya ägare. Använd den övre tabellen för att
            lista vanliga fysiska personer, och den undre för att ta med
            juridiska personer. Det går naturligtvis bra att kombinera de två,
            eller lämna en tom om denna typ av ägare inte ska finnas med.
          </Typography>

          <br />

          <Typography>
            Ange tillsammans med person/organisations-uppgifter även antal
            aktier för varje ägare. Det totala antalet aktier mellan alla ägare
            i det nya bolaget måste uppgå till den totala summan aktier i
            bolaget vilket är{' '}
            <strong>
              500 st om du har valt ett lagerbolag med aktiekapital om 50.000 kr
              och 250 st om du har valt ett lagerbolag med aktiekapital om
              25.000 kr.
            </strong>
          </Typography>

          <br />

          <Typography>
            Lägg till fysiska personer nedan. Klicka på "Radera
            Aktieägare"-knappen för att ta bort tillagda fysiska personer.
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer className={classes.section}>
          <div className={classes.subSection}>
            <FormSectionInfo.InfoBoxTitled title="Fysiska personer" />

            {physicalPeopleFieldArray.fields.length === 0 && (
              <Alert severity="info" variant="outlined">
                <Typography>
                  Du har inte infört några fysiska aktieägare.
                </Typography>
              </Alert>
            )}

            <FormProvider {...formMethods}>
              <Stack gap={16}>
                {physicalPeopleFieldArray.fields.map((field, index) => (
                  <PersonInputGroup key={field.id}>
                    <PhysicalPersonFieldInput
                      index={index}
                      field={field}
                      remove={physicalPeopleFieldArray.remove}
                      fieldArrayName={physicalPeopleFieldArrayName}
                    />
                  </PersonInputGroup>
                ))}
              </Stack>
            </FormProvider>

            <Button
              variant="outlined"
              size="small"
              className={classes.addEntityButton}
              onClick={() => onInsertField(physicalPeopleFieldArray)}
            >
              Lägg till fysisk person
            </Button>
          </div>

          <RadioGroupBoolean
            style={{ margin: '16px 0' }}
            value={showLegalShareholders}
            handleChange={(value) => {
              if (legalShareholdersFieldArray.fields.length === 0) {
                onInsertField(legalShareholdersFieldArray);
                setShowLegalShareholders(value);
                return;
              }

              const { legalShareholders } = formMethods.getValues();

              /**
               * Don't prompt user to confirm if there is no data in any
               * of the legal shareholders objects
               * */
              if (
                legalShareholders
                && !containsNonEmptyObject(legalShareholders)
              ) {
                setShowLegalShareholders(value);
                return;
              }

              const isTogglingOff = !value;

              if (
                isTogglingOff
                // ESLint rule says that alert is obtrusive.
                // I say obtrusive is better than data loss.
                // eslint-disable-next-line no-alert
                && window.confirm(
                  'Detta kommer att radera alla juridiska personer. Vill du fortsätta?',
                )
              ) {
                setShowLegalShareholders(value);
              }
            }}
          />

          <Collapse in={showLegalShareholders}>
            <FormSectionInfo.InfoBoxTitled title="Juridiska personer">
              <Typography>
                Här anges ägare som är juridiska personer, t.ex. företag,
                föreningar eller andra organisationer. Varje juridisk person
                måste också ha en fysisk person som företräder denna vid
                ombildandet som signatör av aktieöverlåtelseavtalet samt som
                ombud på bolagsstämman.
              </Typography>
            </FormSectionInfo.InfoBoxTitled>

            <div style={{ marginBottom: '32px' }}>
              <FormProvider {...formMethods}>
                <Stack gap={16}>
                  {legalShareholdersFieldArray.fields.map((field, index) => (
                    <PersonInputGroup key={field.id}>
                      <LegalShareholderInput
                        field={field}
                        remove={legalShareholdersFieldArray.remove}
                        index={index}
                        fieldArrayName={legalShareholdersFieldArrayName}
                      />
                    </PersonInputGroup>
                  ))}
                </Stack>
              </FormProvider>

              <Button
                variant="outlined"
                size="small"
                className={classes.addEntityButton}
                onClick={() => onInsertField(legalShareholdersFieldArray)}
              >
                Lägg till juridisk enhet
              </Button>
            </div>
          </Collapse>

          <RemainingSharesLabel control={formMethods.control} />
        </FormSectionContainer>
      </div>

      <div>
        <FormSectionInfo.InfoBoxTitled title="Verklig huvudman">
          <Typography>
            Europarlamentets penningtvättsdirektiv kräver ett register över
            verkliga huvudmän. För nystartade bolag ska registreringen ske hos
            Bolagsverket inom 4 veckor.
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer>
          <RHFRadioGroup
            label="Registrera huvudman *"
            control={formMethods.control}
            name="beneficialOwnerHelp"
            defaultValue={RadioConvertUtils.true}
          >
            <RadioLabelled
              value={RadioConvertUtils.true}
              label={`Ja, tack! +${config.fees.beneficalFee} kr (inkluderar Bolagsverkets avgift om 250 kr).`}
            />
            <RadioLabelled
              value={RadioConvertUtils.false}
              label="Nej, jag registrerar själv."
            />
          </RHFRadioGroup>
        </FormSectionContainer>
      </div>

      <OwnerSharesSubmitButton
        isLoading={isUpdating}
        control={formMethods.control}
        onBack={onBack}
        onForward={onForward}
      />
    </Form>
  );
};

function RadioGroupBoolean(props: {
  handleChange: (value: boolean) => void;
  value: boolean;
  style: React.CSSProperties;
}) {
  return (
    <FormControl style={props.style}>
      <FormLabel htmlFor={OwnerSharesFieldNames.hasLegalShareholders}>
        {OwnerSharesFieldLabels.hasLegalShareholders}
      </FormLabel>

      <RadioGroup
        id={OwnerSharesFieldNames.hasLegalShareholders}
        value={props.value ? 'yes' : 'no'}
        defaultValue="no"
        onChange={(event, value) => {
          props.handleChange(value === 'yes');
        }}
      >
        <RadioLabelled value="yes" label="Ja" />
        <RadioLabelled value="no" label="Nej" />
      </RadioGroup>
    </FormControl>
  );
}
